import { useContext, useRef, useCallback, useState, useEffect } from 'react';
import { useAuth, useDefaultSearchCategories } from 'hooks';
import { Tooltip } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';

import {
  resetFilters,
  performSearch,
  setShowVCFundingSortModal,
  cancelSearch,
  setFilters,
} from 'actions/search';
import {
  getHasValidQuery,
  getSearchRequest,
  getSearchView,
  getFiltersByGroupId,
  getFilter,
} from 'selectors/search';
import { Button, Pane, Icon, SearchButton } from 'components/common';
import { colours } from 'utils/theme';
import { aiSearchProductName } from 'utils/constants';
import licenseType from 'models/licenseType';

import Geo from './Geo';
import Organisation from './Organisation';
import Contacts from './Contacts';
import Funding from './Funding';
import Tradeshows from './Tradeshows';
import Trials from './Trials';
import Advanced from './Advanced';
import Lists from './Lists';
import { Container, Content, Actions } from './styled';
import { SearchContext } from '../';
import VentureCapital from './VentureCapital';
import { OrganisationSearch } from '../Results/helpers';
import Acquisitions from './Acquisitions';
import { useCanShowVCFundingSortModal } from '../FundingSortModal/fundingSortModal';
import { NLPSearchTab } from '../../SearchNLP/NLPSearchTab';
import { DisclaimerLabel } from '../../SearchNLP/styled';
import * as strings from '../../SearchNLP/strings';

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 16px;
  gap: 18px;
`;

const Tab = styled.div`
  cursor: pointer;
  border-bottom: 2px solid ${p => (p.active ? '#1890ff' : 'transparent')};
  color: ${p => (p.active ? colours.linkBlue : colours.character.primary85)};
  font-weight: 400;
  display: flex;
  justify-content: center;
  text-align: center;
  align-items: center;
  gap: 6px;
  height: 46px;
  transition: all 0.2s;
  font-size: 14px;
  line-height: 22px;

  &:hover {
    color: #1890ff;
  }
`;

export const BetaBadge = styled.span`
  background-color: ${colours.infoLightBlue};
  color: ${colours.blue};
  padding: 0px 12px;
  border-radius: 4px;
  font-weight: 500;
  font-size: 13px;
  @media (max-width: 1280px) {
    display: none;
  }
`;

const HelpSlot = styled.div`
  margin-left: auto;
  align-self: center;
`;

const Help = () => (
  <Tooltip
    title='Search filters can help you easily refine your search to better target the results you are after.'
    placement='right'
  >
    <div style={{ cursor: 'pointer' }}>
      <Icon icon='help' color={colours.teal} size='xs' />
    </div>
  </Tooltip>
);

const Filters = () => {
  const dispatch = useDispatch();
  const { postSearch, postGeoSearch } = useContext(SearchContext);
  const { loading } = useSelector(getSearchRequest);
  const canShowVCFundingSortModal = useCanShowVCFundingSortModal();
  const { hasLicense, user } = useAuth();
  const hasIndustryLicense = hasLicense([licenseType.industry], true);
  const hasSciSearchAi = user?.hasSciSearchAI ?? false;
  const contentRef = useRef(null);
  const [activeTab, setActiveTab] = useState('filters');
  const aiFilterSessionId = useSelector(getFilter('aiFilterSessionId'));
  const prevAiFilterSessionIdRef = useRef(undefined);

  // When a new SciSearch AI session starts (filters.aiFilterSessionId changes), switch
  // to the SciSearch AI tab so it is the default whenever an AI search is run. This applies
  // even while follow-up queries are blocked off (FOLLOWUP_QUERIES_ENABLED) — but not when
  // the user lacks the SciSearch AI flag, since the tab itself is not rendered then. Only
  // runs on session id transitions so users can switch back to Search Filters while an AI
  // session is still active; clearing the session does not change the tab.
  useEffect(() => {
    if (!hasSciSearchAi) return;
    if (!aiFilterSessionId) {
      prevAiFilterSessionIdRef.current = aiFilterSessionId;
      return;
    }
    if (prevAiFilterSessionIdRef.current !== aiFilterSessionId) {
      setActiveTab('ai');
    }
    prevAiFilterSessionIdRef.current = aiFilterSessionId;
  }, [aiFilterSessionId, hasSciSearchAi]);

  const categoryFilters = useSelector(getFiltersByGroupId('category'));
  const setCategoryFilters = useCallback(
    category => {
      dispatch(
        setFilters(
          {
            category,
          },
          true
        )
      );
    },
    [dispatch]
  );
  const { resetSearchCategoriesToDefault } = useDefaultSearchCategories(
    categoryFilters,
    setCategoryFilters,
    loading
  );

  const handleReset = () => {
    dispatch(resetFilters);
    requestAnimationFrame(() => window.scrollTo(0, 0));
    resetSearchCategoriesToDefault();
  };

  const handleSearch = () => {
    if (canShowVCFundingSortModal) {
      dispatch(setShowVCFundingSortModal(true));
    } else {
      dispatch(performSearch(postSearch, postGeoSearch));
      requestAnimationFrame(() => window.scrollTo(0, 0));
    }
  };

  const hasValidQuery = useSelector(getHasValidQuery);

  const currentView = useSelector(getSearchView);
  const isOrgView = OrganisationSearch(currentView);

  const handleCancel = () => {
    dispatch(cancelSearch());
  };

  return (
    <Container>
      <Pane title='' style={{ paddingBottom: 5 }}>
        {/* Pane suppresses its header (and headerRight) when title is empty, so the
            Help tooltip lives in the tab row instead of the old pane header. */}
        <TabContainer role='tablist'>
          <Tab
            role='tab'
            data-testid='filter-tab-filters'
            aria-selected={activeTab === 'filters'}
            active={activeTab === 'filters'}
            onClick={() => setActiveTab('filters')}
          >
            <Icon icon='filter' size='xs' color={null} />
            Search Filters
          </Tab>
          {hasSciSearchAi && (
            <Tab
              role='tab'
              data-testid='filter-tab-ai'
              aria-selected={activeTab === 'ai'}
              active={activeTab === 'ai'}
              onClick={() => setActiveTab('ai')}
            >
              <Icon icon='sparkles' size='xs' color={null} />
              {aiSearchProductName}
              <BetaBadge>Beta</BetaBadge>
            </Tab>
          )}
          <HelpSlot>
            <Help />
          </HelpSlot>
        </TabContainer>
        {activeTab === 'filters' && (
          <Content
            style={{
              maxHeight: '65vh',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
            ref={contentRef}
          >
            <Geo loading={loading} />
            <Organisation loading={loading} />
            <Contacts loading={loading} />
            <Funding
              loading={loading}
              scrollableContainer={contentRef.current}
            />
            {hasIndustryLicense && <VentureCapital loading={loading} />}
            {hasIndustryLicense && <Acquisitions loading={loading} />}
            <Tradeshows loading={loading} />
            <Trials loading={loading} />
            <Lists loading={loading} />
            <Advanced loading={loading} />
          </Content>
        )}
        {hasSciSearchAi && (
          <div style={{ display: activeTab === 'ai' ? 'block' : 'none' }}>
            <NLPSearchTab />
          </div>
        )}
        <Actions>
          {hasSciSearchAi && activeTab === 'ai' && (
            <DisclaimerLabel style={{ flex: 1 }}>
              {strings.disclaimer}
            </DisclaimerLabel>
          )}
          {activeTab === 'filters' && (
            <Button
              data-testid='filter-reset'
              onClick={handleReset}
              disabled={loading}
            >
              Reset
            </Button>
          )}
          {activeTab === 'filters' && (
            <SearchButton
              type='primary'
              data-testid='filter-update'
              style={{ marginLeft: '10px' }}
              loading={loading}
              disabled={!hasValidQuery || loading}
              onCancel={handleCancel}
              onSearch={handleSearch}
              location={isOrgView ? 'OrganisationSearch' : 'ResearcherSearch'}
            />
          )}
        </Actions>
      </Pane>
    </Container>
  );
};

export default Filters;
